import { Card, FormSection } from '@ksyos/design-system';
import { chain, cloneDeep } from 'lodash';
import { Fragment } from 'react';
import type { IAddCarePathServiceProviderSettings } from '@common/interfaces/addCarePathServicesSettings';
import type { ServiceSettings as IServiceSettings } from '../interfaces/serviceSettings';
import type { IServiceSettingsProps } from '../interfaces/serviceSettingsProps';
import { createSettingsToRenderList } from '../utils/createSettingsToRenderList';
import { SharedServiceSettings } from './SharedServiceSettings';

export function ServiceSettings<T extends IServiceSettings>(props: IServiceSettingsProps<T>) {
    return (
        <Card>
            <Card.Header title={props.title} />
            <Card.Body>{renderListItems(props.settingsQuestionList)}</Card.Body>
        </Card>
    );

    function renderListItems(list: { [key in T as string]: any }) {
        const currentSettings = cloneDeep(props.service.settings);
        const settingsToRender = createSettingsToRenderList(list, currentSettings, props.service.code);

        return chain(settingsToRender)
            .groupBy((i) => i.header)
            .map((setting, header) => {
                return (
                    <Fragment key={header}>
                        <FormSection title={header}>
                            <>
                                {setting.map(({ listKey }) => (
                                    <SharedServiceSettings<T>
                                        currentSettings={currentSettings}
                                        errorPrefix={props.errorPrefix}
                                        errors={props.errors}
                                        key={`${props.service.id}_${listKey}`}
                                        list={list}
                                        listKey={listKey}
                                        onSettingsChange={handleSettingsChange}
                                        requiredProperties={props.requiredProperties}
                                    />
                                ))}
                            </>
                        </FormSection>
                    </Fragment>
                );
            })
            .value();
    }

    function handleSettingsChange(
        value: boolean | string | number | null,
        listKey: keyof IAddCarePathServiceProviderSettings
    ) {
        const settings = {
            ...props.service.settings,
            [listKey]: value,
            ...(listKey === 'isAnalyst' && value && { isBiometrician: false, isGrader: false }),
        };

        props.onChange(settings, `${props.errorPrefix}.${listKey}`);
    }
}
